import { createSlice } from '@reduxjs/toolkit';

import type { CategoriaState } from '@/types/categoria/categoriaTypes';

import {
  atualizarCategoria,
  buscarCategoriaPorId,
  criarCategoria,
  excluirCategoria,
  excluirCategoriaComOpcoes,
  listarCategorias,
} from './actions';

const initialState: CategoriaState = {
  categorias: [],
  currentCategoria: null,
  total: 0,
  page: 1,
  limit: 20,
  isLoading: false,
  error: null,
};

const categoriaSlice = createSlice({
  name: 'categoria',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCategoria: (state) => {
      state.currentCategoria = null;
    },
    clearCategorias: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(listarCategorias.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(listarCategorias.fulfilled, (state, action) => {
        state.isLoading = false;
        const arg = action.meta.arg as
          | { categoriaPaiId?: string | null; page?: number }
          | undefined;
        const isPagedAppend = arg?.page !== undefined && arg.page > 1;
        if (
          (arg && arg.categoriaPaiId !== undefined && arg.categoriaPaiId !== null) ||
          isPagedAppend
        ) {
          const map = new Map(state.categorias.map((c) => [c.id, c] as const));
          for (const item of action.payload.data) {
            map.set(item.id, item);
          }
          state.categorias = Array.from(map.values());
          state.total = Math.max(state.total, action.payload.total);
        } else {
          state.categorias = action.payload.data;
          state.total = action.payload.total;
        }
      })
      .addCase(listarCategorias.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(buscarCategoriaPorId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(buscarCategoriaPorId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCategoria = action.payload;
      })
      .addCase(buscarCategoriaPorId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(criarCategoria.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(criarCategoria.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categorias.unshift(action.payload);
      })
      .addCase(criarCategoria.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(atualizarCategoria.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(atualizarCategoria.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.categorias.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.categorias[index] = action.payload;
        }
        if (state.currentCategoria?.id === action.payload.id) {
          state.currentCategoria = action.payload;
        }
      })
      .addCase(atualizarCategoria.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(excluirCategoria.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(excluirCategoria.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categorias = state.categorias.filter((c) => c.id !== action.payload);
      })
      .addCase(excluirCategoria.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(excluirCategoriaComOpcoes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categorias = state.categorias.filter((c) => c.id !== action.payload.id);
      })
      .addCase(excluirCategoriaComOpcoes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCategorias } = categoriaSlice.actions;
export default categoriaSlice.reducer;
